import { useSyncStore } from '@/lib/store/sync.store';
import type { SyncJobPhase } from '@/lib/cloudflare/types';
import { useEffect, useState, useMemo, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { IconLoader2 } from '@tabler/icons-react';

// 3-phase model with user-friendly copy
type DisplayPhase = 'preparing' | 'processing' | 'finishing' | 'complete';

interface PhaseConfig {
  title: string;
  subtitle: string;
  colorHex: string;
}

const PHASE_CONFIG: Record<DisplayPhase, PhaseConfig> = {
  preparing: {
    title: 'Setting things up',
    subtitle: 'Connecting to Gmail and learning your setup',
    colorHex: '#60a5fa', // blue
  },
  processing: {
    title: 'Sorting your inbox',
    subtitle: 'Analyzing Gmail messages and grouping conversations',
    colorHex: '#f472b6', // pink
  },
  finishing: {
    title: 'Wrapping up',
    subtitle: 'Finalizing and syncing results',
    colorHex: '#fbbf24', // amber
  },
  complete: {
    title: 'All done',
    subtitle: 'Your inbox is organized',
    colorHex: '#4ade80', // green
  },
};

// Rotating detail lines for preparing phase
const PREPARING_DETAILS = [
  'Joining the queue',
  'Reading labels',
  'Scanning filters',
  'Building your model',
];

// Progress weights
const SETUP_WEIGHT = 0.30;
const PROCESSING_WEIGHT = 0.65;
const FINISHING_WEIGHT = 0.05;

// Expected setup time for time-based progress (seconds)
const EXPECTED_SETUP_TIME = 45;

const BOUNCE_SPRING = {
  type: 'spring' as const,
  bounce: 0.4,
};

interface DynamicIslandProps {
  className?: string;
}

// Minimum time (ms) to show each phase before transitioning
const MIN_PHASE_DURATION = 800;

export function DynamicIsland({ className }: DynamicIslandProps) {
  const { progress, isPolling, syncStartedAt } = useSyncStore();
  const [displayPhase, setDisplayPhase] = useState<DisplayPhase>('preparing');
  const [isVisible, setIsVisible] = useState(false);
  const [lastPhaseChange, setLastPhaseChange] = useState(0);
  const [preparingDetailIndex, setPreparingDetailIndex] = useState(0);
  const [finishingStartTime, setFinishingStartTime] = useState<number | null>(null);
  const [simulatedProgress, setSimulatedProgress] = useState(0);

  // Track if sync is actively processing
  const isActive = isPolling && progress?.status === 'processing';
  const isCompleted = progress?.status === 'completed';

  // Map backend phase to display phase
  const backendPhase = progress?.phase;
  const actualDisplayPhase: DisplayPhase = useMemo(() => {
    if (isCompleted) return 'complete';
    if (!backendPhase) return 'preparing';

    // queued, learning -> preparing
    if (backendPhase === 'queued' || backendPhase === 'learning') {
      return 'preparing';
    }
    // analyzing -> processing
    if (backendPhase === 'analyzing') {
      return 'processing';
    }
    // completing -> finishing
    if (backendPhase === 'completing') {
      return 'finishing';
    }
    return 'preparing';
  }, [backendPhase, isCompleted]);

  // Reset finishing start time when sync becomes inactive
  useEffect(() => {
    if (!isActive) {
      setFinishingStartTime(null);
    }
  }, [isActive]);

  // Track finishing start time
  useEffect(() => {
    if (actualDisplayPhase === 'finishing' && !finishingStartTime) {
      setFinishingStartTime(Date.now());
    }
  }, [actualDisplayPhase, finishingStartTime]);

  // Simulate progress during preparing phase (time-based)
  // Uses syncStartedAt from store which survives page refresh
  useEffect(() => {
    if (actualDisplayPhase !== 'preparing' || !syncStartedAt) return;

    const interval = setInterval(() => {
      const elapsed = (Date.now() - syncStartedAt) / 1000;
      const setupProgress = Math.min(elapsed / EXPECTED_SETUP_TIME, 1) * SETUP_WEIGHT;
      setSimulatedProgress(setupProgress);
    }, 100);

    return () => clearInterval(interval);
  }, [actualDisplayPhase, syncStartedAt]);

  // Calculate weighted progress
  const weightedProgress = useMemo(() => {
    if (actualDisplayPhase === 'complete') return 1;

    if (actualDisplayPhase === 'preparing') {
      return simulatedProgress;
    }

    if (actualDisplayPhase === 'processing') {
      const realProgress = progress?.total && progress.total > 0
        ? progress.processed / progress.total
        : 0;
      // setupWeight base + real processing progress
      const computed = SETUP_WEIGHT + realProgress * PROCESSING_WEIGHT;
      // Cap at 0.95 to avoid jumping into finishing early
      return Math.min(computed, 0.95);
    }

    if (actualDisplayPhase === 'finishing') {
      if (!finishingStartTime) return 0.95;
      const elapsed = (Date.now() - finishingStartTime) / 1000;
      const finishingProgress = Math.min(elapsed / 2, 1); // 2 seconds expected
      return 0.95 + finishingProgress * FINISHING_WEIGHT;
    }

    return 0;
  }, [actualDisplayPhase, simulatedProgress, progress?.processed, progress?.total, finishingStartTime]);

  // Rotate preparing details every 2.5 seconds
  // Initialize index based on elapsed time to survive page refresh
  useEffect(() => {
    if (displayPhase !== 'preparing') return;

    // If sync hasn't started, just cycle from the beginning
    if (!syncStartedAt) {
      const intervalId = setInterval(() => {
        setPreparingDetailIndex(prev => (prev + 1) % PREPARING_DETAILS.length);
      }, 2500);
      return () => clearInterval(intervalId);
    }

    // Calculate current state and schedule updates precisely
    const elapsed = Date.now() - syncStartedAt;
    setPreparingDetailIndex(Math.floor(elapsed / 2500) % PREPARING_DETAILS.length);

    // Calculate remaining time until next message change
    const timeUntilNextChange = 2500 - (elapsed % 2500);
    let intervalId: ReturnType<typeof setInterval>;

    const timeoutId = setTimeout(() => {
      setPreparingDetailIndex(prev => (prev + 1) % PREPARING_DETAILS.length);
      intervalId = setInterval(() => {
        setPreparingDetailIndex(prev => (prev + 1) % PREPARING_DETAILS.length);
      }, 2500);
    }, timeUntilNextChange);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [displayPhase, syncStartedAt]);

  // Show immediately when active, hide with delay after completion
  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
    } else if (isCompleted) {
      // Keep showing for 1.5s after completion
      const timer = setTimeout(() => setIsVisible(false), 1500);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isActive, isCompleted]);

  // Smooth phase transition with minimum duration
  useEffect(() => {
    if (actualDisplayPhase === displayPhase) return;

    const now = Date.now();
    const timeSinceLastChange = now - lastPhaseChange;

    if (timeSinceLastChange >= MIN_PHASE_DURATION) {
      setDisplayPhase(actualDisplayPhase);
      setLastPhaseChange(now);
    } else {
      const delay = MIN_PHASE_DURATION - timeSinceLastChange;
      const timer = setTimeout(() => {
        setDisplayPhase(actualDisplayPhase);
        setLastPhaseChange(Date.now());
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [actualDisplayPhase, displayPhase, lastPhaseChange]);

  const config = PHASE_CONFIG[displayPhase];
  const percentComplete = Math.round(weightedProgress * 100);

  if (!isVisible) return null;

  return (
    <div className={className}>
      <motion.div
        className="mx-auto overflow-hidden rounded-full bg-black"
        layout
        initial={false}
        style={{ borderRadius: 16 }}
        transition={BOUNCE_SPRING}
      >
        <motion.div
          className="flex items-center gap-3 px-4 h-8"
          layout
          initial={false}
        >
          {/* Spinning loader icon */}
          <motion.div
            animate={{ color: config.colorHex }}
            transition={{ duration: 0.2 }}
            className="shrink-0"
          >
            <IconLoader2 className="h-4 w-4 animate-spin" style={{ color: 'inherit' }} />
          </motion.div>

          {/* Content - single line: Title · Subtitle */}
          <AnimatePresence mode="popLayout">
            <motion.p
              key={`${displayPhase}-${preparingDetailIndex}`}
              className="text-xs text-white whitespace-nowrap"
              initial={{ opacity: 0, filter: 'blur(4px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, filter: 'blur(4px)', position: 'absolute' }}
              transition={{ duration: 0.15 }}
            >
              <span className="font-medium">{config.title}</span>
              <span className="text-white/40 mx-1.5">·</span>
              <span className="text-white/60">
                {displayPhase === 'preparing' ? (
                  PREPARING_DETAILS[preparingDetailIndex]
                ) : displayPhase === 'processing' && progress?.total ? (
                  `${progress.processed || 0} of ${progress.total} messages`
                ) : (
                  config.subtitle
                )}
              </span>
            </motion.p>
          </AnimatePresence>

          {/* Progress indicator */}
          <div className="flex items-center gap-1.5 shrink-0">
            <div className="h-1 w-14 overflow-hidden rounded-full bg-white/20">
              <motion.div
                className="h-full"
                animate={{ width: `${percentComplete}%`, backgroundColor: config.colorHex }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <span className="text-white/50 text-[10px] font-mono w-6 text-right">
              {percentComplete}%
            </span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
